// The org chart editor's review (spec 06 §Main screens 7: "simulate" diff before apply). Opening
// it runs `POST org-chart/simulate` with the draft's body; the dialog names every reporting line
// that moves, every authority or delegation line added, re-policied or removed, the reasons the
// API would refuse it, and the teams the move takes out of shape (advice only). "Apply" sends
// the same body to `PUT org-chart/edges`. The body replaces every line of the kinds it touches
// (last writer wins, M1-04), which is why the diff comes first: a line another editor added since
// this chart was read shows up here as a removal before anything is written.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Notice,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRightIcon } from "lucide-react";
import { useEffect } from "react";
import type { Api } from "../../api/client";
import { keys } from "../queries";
import { FormError, problemText } from "../structure/parts";
import {
  adviceAfter,
  bodyFor,
  type DraftOp,
  EDGE_LABELS,
  LINE_VERBS,
  type LineKind,
  type OrgChart,
  type Simulation,
} from "./chart-model";
import { chartKeys } from "./chart-queries";

export function ReviewDialog({
  api,
  companyId,
  chart,
  ops,
  onClose,
  onApplied,
}: {
  api: Api;
  companyId: string;
  chart: OrgChart;
  ops: readonly DraftOp[];
  onClose: () => void;
  onApplied: () => void;
}) {
  const queryClient = useQueryClient();
  const body = bodyFor(chart, ops);
  const simulate = useMutation({
    mutationFn: () => api.client.orgChart.simulate({ params: { co: companyId }, body }),
  });
  const apply = useMutation({
    mutationFn: () => api.client.orgChart.putEdges({ params: { co: companyId }, body }),
    onSuccess: (next) => {
      queryClient.setQueryData(chartKeys.orgChart(companyId), next);
      // Seats carry `supervisor_seat_id` too (Setup › Seats, the seat drawer).
      void queryClient.invalidateQueries({ queryKey: keys.seats(companyId) });
      onApplied();
    },
  });
  // The diff is computed once, when the dialog opens (the draft cannot change under it).
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once per open
  useEffect(() => {
    simulate.mutate();
  }, []);

  const title = (id: string | null) =>
    id === null
      ? "the top (no manager)"
      : (chart.seats.find((s) => s.id === id)?.title ?? "a seat");
  const advice = adviceAfter(chart, ops);
  const sim = simulate.data;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[640px]" data-slot="chart-review">
        <DialogHeader>
          <DialogTitle>Review the chart changes</DialogTitle>
          <DialogDescription>
            What saving would change, checked by the server. Saving replaces every{" "}
            {body.kinds?.map((k) => EDGE_LABELS[k].toLowerCase()).join(", ")} line of the chart with
            the lines shown here.
          </DialogDescription>
        </DialogHeader>
        {simulate.isPending ? (
          <p role="status" className="text-sm text-muted-foreground">
            Checking the changes…
          </p>
        ) : simulate.isError ? (
          <Notice tone="danger" role="alert" title="The changes could not be checked">
            {problemText(simulate.error)}
          </Notice>
        ) : sim ? (
          <Diff sim={sim} title={title} />
        ) : null}
        {advice.length > 0 ? (
          <Notice tone="warning" title="Team shape (advice, not a rule)" data-slot="team-advice">
            <ul className="flex flex-col gap-1">
              {advice.map((a) => (
                <li key={a.team.id}>
                  <span className="font-medium">{a.team.name}</span>: {a.advice}
                </li>
              ))}
            </ul>
          </Notice>
        ) : null}
        <FormError what="Saving the chart" error={apply.error} />
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Keep editing
          </Button>
          <Button
            disabled={!sim?.valid || apply.isPending || isEmpty(sim)}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? "Saving…" : "Apply changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Nothing would change (the draft only restates the chart). */
function isEmpty(sim: Simulation | undefined): boolean {
  if (!sim) return true;
  const lines = (l: { kind: string }[]) => l.some((e) => e.kind !== "reports_to");
  return (
    sim.supervisor_changes.length === 0 &&
    !lines(sim.added) &&
    !lines(sim.updated) &&
    !lines(sim.removed)
  );
}

function Diff({ sim, title }: { sim: Simulation; title: (id: string | null) => string }) {
  // `reports_to` rows only mirror `supervisor_seat_id`; the supervisor changes say what moves.
  const lines = <T extends { kind: string }>(l: T[]) =>
    l.filter((e): e is T & { kind: LineKind } => e.kind !== "reports_to");
  const added = lines(sim.added);
  const updated = lines(sim.updated);
  const removed = lines(sim.removed);
  const policy = (p: Record<string, unknown>) => {
    const parts = [
      p.max_depth !== undefined ? `depth ${p.max_depth}` : null,
      p.budget_micros !== undefined ? `budget ${Number(p.budget_micros) / 1_000_000}` : null,
      Array.isArray(p.allowed_task_kinds) ? `kinds ${p.allowed_task_kinds.join(", ")}` : null,
    ].filter(Boolean);
    return parts.length ? ` (${parts.join(", ")})` : "";
  };
  return (
    <div className="flex min-w-0 flex-col gap-4 text-sm" data-slot="chart-diff">
      {sim.problems.length > 0 ? (
        <Notice tone="danger" role="alert" title="The server would refuse this">
          <ul className="flex flex-col gap-1">
            {sim.problems.map((p) => (
              <li key={`${p.code}-${p.seat_ids.join(",")}`}>
                {p.message}
                {p.seat_ids.length ? `: ${p.seat_ids.map((id) => title(id)).join(", ")}` : ""}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {sim.supervisor_changes.length > 0 ? (
        <section aria-label="Reporting lines" className="flex flex-col gap-1.5">
          <h3 className="font-medium text-foreground">Reporting lines</h3>
          <ul className="flex flex-col gap-1.5">
            {sim.supervisor_changes.map((c) => (
              <li
                key={c.seat_id}
                data-change="reports_to"
                className="flex flex-wrap items-center gap-1.5 text-secondary"
              >
                <span className="font-medium text-foreground">{title(c.seat_id)}</span>
                <span>reports to</span>
                <span className="line-through decoration-faint">{title(c.from)}</span>
                <ArrowRightIcon aria-label="now" className="size-3.5 text-faint" />
                <span className="font-medium text-foreground">{title(c.to)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {added.length + updated.length + removed.length > 0 ? (
        <section aria-label="Authority and delegation" className="flex flex-col gap-1.5">
          <h3 className="font-medium text-foreground">Authority and delegation</h3>
          <ul className="flex flex-col gap-1.5">
            {added.map((e) => (
              <li key={`a-${e.kind}-${e.from_seat_id}-${e.to_seat_id}`} data-change="added">
                <Badge variant="success">Added</Badge> {title(e.from_seat_id)}{" "}
                {LINE_VERBS[e.kind].toLowerCase()} {title(e.to_seat_id)}
                {policy(e.policy ?? {})}
              </li>
            ))}
            {updated.map((e) => (
              <li key={`u-${e.id}`} data-change="updated">
                <Badge variant="info">Changed</Badge> {title(e.from_seat_id)}{" "}
                {LINE_VERBS[e.kind].toLowerCase()} {title(e.to_seat_id)}
                {policy(e.policy)}
              </li>
            ))}
            {removed.map((e) => (
              <li key={`r-${e.id}`} data-change="removed">
                <Badge variant="danger">Removed</Badge> {title(e.from_seat_id)}{" "}
                {LINE_VERBS[e.kind].toLowerCase()} {title(e.to_seat_id)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {isEmpty(sim) && sim.problems.length === 0 ? (
        <p className="text-muted-foreground">
          Nothing would change: the chart already looks like this.
        </p>
      ) : null}
    </div>
  );
}
